import { useState, useEffect } from 'react';
import { useUserContext } from '../context/use-user-context';
import { Featureflag } from '../constants/featureFlags';

interface UseDelayedLoadOptions {
  /**
   * Seconds to delay visibility.
   * - `undefined` → config not loaded yet, stay hidden
   * - `0` → no delay, show immediately
   * - `> 0` → show after delay
   */
  delaySeconds?: number;
}

interface UseDelayedLoadReturn {
  isVisible: boolean;
}

/**
 * Hook that delays widget visibility by a configurable duration.
 * Stays hidden while `delaySeconds` is `undefined` (config not yet loaded),
 * shows immediately when `0`, or after the specified delay.
 * Gated by the DELAYED_LOAD feature flag — when off the delay is skipped
 * but the config-loading guard (`undefined`) is still respected.
 */
export const useDelayedLoad = ({
  delaySeconds,
}: UseDelayedLoadOptions): UseDelayedLoadReturn => {
  const { isFeatureEnabled } = useUserContext();
  const isFlagEnabled = isFeatureEnabled(Featureflag.DELAYED_LOAD);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (delaySeconds === undefined) return;

    if (!isFlagEnabled || !delaySeconds) {
      setIsVisible(true);
      return;
    }

    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, delaySeconds * 1000);

    return () => clearTimeout(timeout);
  }, [delaySeconds, isFlagEnabled]);

  return { isVisible };
};
